import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Play, Volume2 } from 'lucide-react';
import { parseVideoUrl } from '../../lib/videos';
import { useVideoThumbnail } from '../../lib/useVideoThumbnail';
import { loadVimeoApi, loadYouTubeApi } from '../../lib/videoApis';

export type VideoMilestone = 'video_play' | 'video_progress_50' | 'video_complete';

interface DemoVideoProps {
    /** YouTube, Vimeo or Loom link, or a self-hosted file such as `/demo.mp4`. */
    url: string;
    title: string;
    /** Poster override — otherwise the provider's thumbnail, then a gradient. */
    poster?: string;
    /** Called at most once per milestone. `provider` is `youtube`, `vimeo`, `loom` or `file`. */
    onMilestone?: (milestone: VideoMilestone, provider: string) => void;
    /** Shown in place of the play button when `url` is empty or unrecognised. */
    fallback?: React.ReactNode;
    className?: string;
}

const FILE_PATTERN = /\.(mp4|webm|m4v|mov)(?:[?#]|$)/i;

type Report = (milestone: VideoMilestone) => void;

/** Watches a YouTube iframe (loaded with `enablejsapi=1`) for the halfway mark and the end. */
function trackYouTube(iframe: HTMLIFrameElement, report: Report): () => void {
    let cancelled = false;
    let timer: number | undefined;

    loadYouTubeApi()
        .then((YT) => {
            if (cancelled) return;
            const player = new YT.Player(iframe, {
                // The iframe is served from youtube-nocookie, and the API only
                // talks to the host it's told about.
                host: new URL(iframe.src).origin,
                events: {
                    onReady: () => {
                        timer = window.setInterval(() => {
                            const duration = player.getDuration();
                            if (duration > 0 && player.getCurrentTime() / duration >= 0.5) {
                                report('video_progress_50');
                                window.clearInterval(timer);
                            }
                        }, 1000);
                    },
                    onStateChange: ({ data }) => {
                        if (data !== YT.PlayerState.ENDED) return;
                        report('video_progress_50');
                        report('video_complete');
                    },
                },
            });
        })
        .catch(() => {/* tracking is best-effort */});

    return () => {
        cancelled = true;
        window.clearInterval(timer);
    };
}

function trackVimeo(iframe: HTMLIFrameElement, report: Report): () => void {
    let cancelled = false;
    let detach = () => {};

    loadVimeoApi()
        .then((Vimeo) => {
            if (cancelled) return;
            const player = new Vimeo.Player(iframe);
            player.on('timeupdate', ({ percent }) => {
                if (percent >= 0.5) report('video_progress_50');
            });
            player.on('ended', () => {
                report('video_progress_50');
                report('video_complete');
            });
            detach = () => {
                player.off('timeupdate');
                player.off('ended');
            };
        })
        .catch(() => {/* tracking is best-effort */});

    return () => {
        cancelled = true;
        detach();
    };
}

/**
 * Large 16:9 demo player inside a glowing gradient frame. Shows a poster with
 * a play button first and never autoplays; YouTube/Vimeo/Loom iframes are only
 * mounted once the viewer presses play.
 */
const DemoVideo: React.FC<DemoVideoProps> = ({ url, title, poster, onMilestone, fallback, className = '' }) => {
    const isFile = FILE_PATTERN.test(url);
    const embed = useMemo(() => (isFile ? null : parseVideoUrl(url)), [isFile, url]);
    const provider = isFile ? 'file' : embed?.provider;
    const thumb = useVideoThumbnail(isFile ? '' : url, poster);

    const [playing, setPlaying] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const fired = useRef(new Set<VideoMilestone>());
    const onMilestoneRef = useRef(onMilestone);
    onMilestoneRef.current = onMilestone;

    const report = useCallback<Report>(
        (milestone) => {
            if (!provider || fired.current.has(milestone)) return;
            fired.current.add(milestone);
            onMilestoneRef.current?.(milestone, provider);
        },
        [provider]
    );

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!playing || !iframe) return;
        if (provider === 'youtube') return trackYouTube(iframe, report);
        if (provider === 'vimeo') return trackVimeo(iframe, report);
    }, [playing, provider, report]);

    const start = () => {
        setPlaying(true);
        report('video_play');
        // Call play() inside the click so mobile browsers allow sound.
        videoRef.current?.play().catch(() => {/* the native controls take over */});
    };

    const onTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
        const { currentTime, duration } = e.currentTarget;
        if (duration > 0 && currentTime / duration >= 0.5) report('video_progress_50');
    };

    const iframeSrc =
        embed?.provider === 'youtube'
            ? `${embed.embedUrl}&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`
            : embed?.embedUrl;

    const playable = isFile || Boolean(embed);

    return (
        <div className={`relative ${className}`}>
            {/* glow */}
            <div
                aria-hidden
                className="absolute -inset-3 sm:-inset-6 rounded-[2rem] bg-gradient-to-r from-brand-cyan/40 via-sky-400/30 to-brand-blue/40 blur-2xl opacity-60 pointer-events-none"
            />
            {/* gradient border */}
            <div className="relative rounded-2xl sm:rounded-[1.35rem] p-px bg-gradient-to-br from-brand-cyan via-sky-400/60 to-brand-blue shadow-lift">
                <div className="relative aspect-video overflow-hidden rounded-[15px] sm:rounded-[1.3rem] bg-navy-900">
                    {isFile && (
                        <video
                            ref={videoRef}
                            src={url}
                            poster={poster}
                            preload="none"
                            playsInline
                            controls={playing}
                            aria-label={title}
                            onTimeUpdate={onTimeUpdate}
                            onEnded={() => {
                                report('video_progress_50');
                                report('video_complete');
                            }}
                            className="absolute inset-0 w-full h-full bg-black"
                        />
                    )}

                    {playing && iframeSrc && (
                        <iframe
                            ref={iframeRef}
                            src={iframeSrc}
                            title={title}
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                            allowFullScreen
                        />
                    )}

                    {!playing && (
                        <div className="absolute inset-0">
                            {thumb.src ? (
                                <img
                                    src={thumb.src}
                                    alt=""
                                    onError={thumb.onError}
                                    onLoad={thumb.onLoad}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            ) : (
                                <PosterPlaceholder />
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-navy-950/20 to-navy-950/10" />

                            {playable ? (
                                <button
                                    type="button"
                                    onClick={start}
                                    aria-label={`Play video: ${title}`}
                                    className="group absolute inset-0 w-full h-full grid place-items-center focus-visible:outline-none"
                                >
                                    <span className="relative grid place-items-center">
                                        <span
                                            aria-hidden
                                            className="absolute inset-0 rounded-full bg-brand-cyan/40 animate-ping [animation-duration:2.2s]"
                                        />
                                        <span className="relative grid place-items-center w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-brand-cyan to-brand-blue text-white shadow-glow-cyan ring-4 ring-white/10 transition-transform duration-300 group-hover:scale-110 group-focus-visible:ring-white/70">
                                            <Play className="w-8 h-8 sm:w-10 sm:h-10 translate-x-0.5 fill-white" />
                                        </span>
                                    </span>
                                    <span className="absolute bottom-3 left-3 sm:bottom-5 sm:left-5 inline-flex items-center gap-1.5 rounded-full glass px-3 py-1.5 text-xs sm:text-sm font-medium text-white">
                                        <Volume2 className="w-4 h-4 text-brand-cyan" />
                                        Sound on
                                    </span>
                                </button>
                            ) : (
                                <div className="absolute inset-0 grid place-items-center p-6 text-center text-sm text-text-muted">
                                    {fallback}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/** Stand-in poster: brand gradient over the blueprint grid, with a call waveform. */
const WAVE = [30, 55, 40, 75, 50, 90, 60, 100, 70, 45, 85, 55, 95, 65, 40, 80, 50, 70, 35, 60, 45, 25];

const PosterPlaceholder: React.FC = () => (
    <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-brand-blue/30 via-navy-800 to-brand-cyan/20">
        <div className="absolute inset-0 bg-grid bg-[length:40px_40px] opacity-60" />
        <div className="absolute inset-x-0 bottom-[16%] flex items-end justify-center gap-1 sm:gap-1.5 h-[14%] opacity-50">
            {WAVE.map((h, i) => (
                <span
                    key={i}
                    className="w-1 sm:w-1.5 rounded-full bg-gradient-to-t from-brand-blue to-brand-cyan"
                    style={{ height: `${h}%` }}
                />
            ))}
        </div>
    </div>
);

export default DemoVideo;
